import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Root, Portal, Overlay, Content, Title, Description } from '@radix-ui/react-dialog';
import { useSearchParams } from 'react-router-dom';

import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import SignatureCanvas, { stripSignatureDataUrl } from '@/components/ui/SignatureCanvas/SignatureCanvas';
import { RegulationsAcceptanceStatusDtoIn } from '@/models/in/RegulationsAcceptanceStatusDtoIn';
import { RegulationsAcceptanceDtoOut } from '@/models/out/RegulationsAcceptanceDtoOut';
import { FUND_RULES_CONFIRMATION_LABEL, FUND_RULES_DIALOG_TITLE } from '@/data/fundRulesDeclarations';
import { NEW_VIEWER_BASENAME } from '@/helpers/sessionPublicPath';
import { REGULATIONS_PAGE_PATH } from '@/pages/Regulations/RegulationsPage';
import FundRulesGrid from './FundRulesGrid';
import { rulesIcon, strokeProps } from './fundRuleGlyphs';
import styles from './RegulationsAcceptanceDialog.module.scss';

const REGULATIONS_ACCEPTANCE_STATUS_URL = 'regulations-acceptance/status';
const REGULATIONS_ACCEPTANCE_URL = 'regulations-acceptance';
const REGULATIONS_DISMISS_URL = 'regulations-acceptance/dismiss';

const REGULATIONS_CONFIRMATION_LABEL = 'קראתי את כל נהלי העמותה וההנחיות, ואני מתחייב להתנהל על פיהם.';

/**
 * Opens the dialog for someone it does not apply to, so they can see what a coordinator sees
 * (HAT-1256, on the reporter's request: "I want to see how it looks to a user").
 *
 * Administrators only, and nothing in a preview is written: no signature is stored, no copy is
 * mailed, and closing it does not spend anybody's one dismissal. The alternative the reporter was
 * about to reach for - impersonating a coordinator with "login as user" - would have done all
 * three in that coordinator's name, and a signature there is a real consent to the fund rules.
 */
export const REGULATIONS_PREVIEW_PARAM = 'regulations-preview';

/**
 * "אישור כללי הקרן" as the portal shows it on entry (HAT-1256): a coordinator confirms the fund
 * rules and the organization's regulations, types their name and signs, once.
 *
 * Closable exactly once. The reporter asked for the dialog to "allow being ignored once and block
 * the site from then on", so the first showing carries a close button and every later one does not -
 * no X, no escape, no click-away. The one way past a blocking dialog is to sign.
 *
 * Whether the escape is still available is the server's answer (`canDismiss`), not a count kept
 * here: "once" has to mean once per coordinator, and anything held in the browser is refreshed by
 * clearing site data or opening another browser, which is the thing the request is against.
 *
 * Who is asked is also the server's decision (see RegulationsAcceptanceService.AppliesTo): office
 * staff and administrators are answered "already accepted", so this component only ever reads flags
 * and never re-implements the rules behind them.
 */
const RegulationsAcceptanceDialog = () => {
  const { t } = useTranslation();
  const { currentUser, isCurrentUserAdmin } = useAuthContext();
  const [searchParams] = useSearchParams();
  const signedInAs = currentUser?.email;
  const previewRequested = searchParams.get(REGULATIONS_PREVIEW_PARAM) === '1' && isCurrentUserAdmin();
  /*
   * The parameter opens the preview; from there on it belongs to the visit and to the account that
   * opened it, and not to the address bar.
   *
   * The address an administrator is given is the site root, and the root is a pair of redirects:
   * FundProvider sends "/" on to the last fund used, and that fund on to its home screen. Neither
   * carries the query string. Re-reading the parameter on every render therefore took the dialog off
   * the screen a moment after it appeared - "מפנה אותי לעמוד הרלוונטי אך מייד זה נעלם", as the
   * reporter put it (HAT-1256). Holding it by account, rather than as a plain flag, keeps a real
   * coordinator from inheriting a preview if the session changes underneath it.
   */
  const [previewFor, setPreviewFor] = useState(previewRequested ? signedInAs : undefined);
  useEffect(() => {
    if (previewRequested) setPreviewFor(signedInAs);
  }, [previewRequested, signedInAs]);
  const isPreview = previewFor !== undefined && previewFor === signedInAs;

  const [signerName, setSignerName] = useState('');
  const [signatureBase64, setSignatureBase64] = useState('');
  const [declarationsAccepted, setDeclarationsAccepted] = useState(false);
  const [regulationsAccepted, setRegulationsAccepted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Closed by hand for this visit. Nothing is stored, so the next visit asks again. */
  const [isDismissed, setIsDismissed] = useState(false);
  /*
   * Signing takes the dialog off the screen immediately, without waiting for the status query to
   * come back: the mutation's own 200 is the answer, and a second of a dialog sitting over a
   * finished signature reads as a failure.
   */
  const [justSigned, setJustSigned] = useState(false);
  /** The preview's submit was pressed. There is nothing to save, so the dialog says so and stays. */
  const [previewSubmitted, setPreviewSubmitted] = useState(false);

  const { data: status } = useGetAuthApi<RegulationsAcceptanceStatusDtoIn>(
    currentUser ? REGULATIONS_ACCEPTANCE_STATUS_URL : '',
  );

  const { mutate: accept, isPending } = usePostAuthApi<RegulationsAcceptanceStatusDtoIn, RegulationsAcceptanceDtoOut>(
    REGULATIONS_ACCEPTANCE_URL,
  );

  const { mutate: recordDismissal } = usePostAuthApi<RegulationsAcceptanceStatusDtoIn, object>(REGULATIONS_DISMISS_URL);

  // A different person signing in is a different dialog: nothing the previous one typed, and no
  // dismissal of theirs, may carry over.
  useEffect(() => {
    setSignerName('');
    setSignatureBase64('');
    setDeclarationsAccepted(false);
    setRegulationsAccepted(false);
    setSubmitError(null);
    setIsDismissed(false);
    setJustSigned(false);
    setPreviewSubmitted(false);
  }, [signedInAs]);

  const nameInputRef = useRef<HTMLInputElement>(null);

  if (!currentUser || isDismissed || justSigned) return null;
  /*
   * Shown only when the server says, in so many words, that this person has not signed.
   *
   * Deliberately fail-open: while the status is in flight, if the call fails, or if the answer is
   * not the shape expected, nothing appears. Putting a dialog over someone who signed months ago
   * because a request timed out is worse than missing one signature - and nothing is lost by
   * waiting, because the confirmation is asked for again on their next visit and the endpoint
   * itself refuses an unconfirmed submission either way.
   */
  if (!isPreview && status?.isAccepted !== false) return null;

  /*
   * The one escape the reporter asked to allow. Absent or false - an older server that does not send
   * the flag included - the dialog blocks, which is the side to fail on for a gate.
   */
  const canDismiss = isPreview || status?.canDismiss === true;

  const dismiss = () => {
    setIsDismissed(true);
    // A preview spends nothing: an administrator looking at the dialog must not use up the one
    // dismissal of the account they happen to be signed in as.
    if (isPreview) return;
    // Spends the escape on the server, so the next visit gets the blocking dialog. Nothing is done
    // with the answer: the dialog is already off the screen, and a failed call simply leaves the
    // coordinator another dismissal, which is the harmless direction to get this wrong in.
    recordDismissal({});
  };

  const trimmedName = signerName.trim();
  const canSubmit =
    trimmedName.length > 0 && signatureBase64.length > 0 && declarationsAccepted && regulationsAccepted && !isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitError(null);

    if (isPreview) {
      setPreviewSubmitted(true);
      return;
    }

    accept(
      { signerName: trimmedName, signatureBase64, declarationsAccepted, regulationsAccepted },
      {
        onSuccess: () => setJustSigned(true),
        onError: () => setSubmitError('שמירת האישור נכשלה. יש לנסות שוב, ואם התקלה חוזרת - לפנות למשרד.'),
      },
    );
  };

  return (
    <Root open onOpenChange={(isOpen) => !isOpen && canDismiss && dismiss()}>
      <Portal>
        <Overlay className={styles.overlay} />
        <Content
          className={styles.content}
          // Once the escape is spent, every way out of a Radix dialog is closed off: the dialog is
          // passed by signing. Left alone on the first showing, where the X is the intended exit.
          onEscapeKeyDown={(e) => !canDismiss && e.preventDefault()}
          onPointerDownOutside={(e) => !canDismiss && e.preventDefault()}
          onInteractOutside={(e) => !canDismiss && e.preventDefault()}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            nameInputRef.current?.focus();
          }}
        >
          {canDismiss && (
            <button type="button" className={styles.closeButton} onClick={dismiss} aria-label={t('close')}>
              <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                <path d="M5.5 18.5 18.5 5.5M18.5 18.5 5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          )}

          <div className={styles.body}>
            {isPreview && (
              <div className={styles.previewBanner}>
                <strong>תצוגה מקדימה.</strong> כך נראית החלונית לרכז קרן. שום דבר כאן אינו נשמר - לא החתימה, לא
                המייל ולא סגירת החלונית. אצל רכז, מהפעם השנייה ואילך כפתור הסגירה (X) אינו מופיע והחלונית חוסמת
                את האתר עד לחתימה.
              </div>
            )}
            <div className={styles.header}>
              <div className={styles.titleRow}>
                <span className={styles.titleIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                    {rulesIcon}
                  </svg>
                </span>
                <Title className={`${styles.title} heading-h4-semibold`}>{FUND_RULES_DIALOG_TITLE}</Title>
              </div>
              {/* The second showing says why there is no longer a way out, rather than leaving a
                  coordinator hunting for the X that was there last time. */}
              <Description className={styles.subtitle}>
                {canDismiss
                  ? 'יש לקרוא את הכללים הבאים, לאשר אותם ולחתום. עותק מהאישור יישלח אליך במייל.'
                  : 'כדי להמשיך לעבוד באתר יש לקרוא את הכללים הבאים, לאשר אותם ולחתום. עותק מהאישור יישלח אליך במייל.'}
              </Description>
            </div>

            <div className={styles.scroller}>
              <FundRulesGrid />

              <div className={styles.confirmations}>
                <label className={styles.confirmLabel}>
                  <input
                    type="checkbox"
                    className={styles.confirmInput}
                    checked={declarationsAccepted}
                    onChange={(e) => setDeclarationsAccepted(e.target.checked)}
                  />
                  <span className={styles.confirmBox}>
                    <svg className={styles.confirmCheck} width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 12.5 10 16.5l8-9" {...strokeProps} strokeWidth={3} />
                    </svg>
                  </span>
                  <span className={styles.confirmText}>{FUND_RULES_CONFIRMATION_LABEL}</span>
                </label>

                <label className={styles.confirmLabel}>
                  <input
                    type="checkbox"
                    className={styles.confirmInput}
                    checked={regulationsAccepted}
                    onChange={(e) => setRegulationsAccepted(e.target.checked)}
                  />
                  <span className={styles.confirmBox}>
                    <svg className={styles.confirmCheck} width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 12.5 10 16.5l8-9" {...strokeProps} strokeWidth={3} />
                    </svg>
                  </span>
                  <span className={styles.confirmText}>
                    {REGULATIONS_CONFIRMATION_LABEL}{' '}
                    {/* A new tab, not a route change: the reader comes back to a dialog that still
                        holds everything they have already filled in. */}
                    <a
                      className={styles.regulationsLink}
                      href={`${NEW_VIEWER_BASENAME}${REGULATIONS_PAGE_PATH}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      לקריאת הנהלים המלאים
                    </a>
                  </span>
                </label>
              </div>

              <div className={styles.signatureSection}>
                <label className={styles.fieldLabel} htmlFor="regulations-signer-name">
                  שם החותם
                </label>
                <input
                  id="regulations-signer-name"
                  ref={nameInputRef}
                  className={styles.nameInput}
                  type="text"
                  maxLength={200}
                  autoComplete="name"
                  placeholder="שם מלא"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                />

                <span className={styles.fieldLabel}>חתימה</span>
                <span className={styles.fieldHint}>יש לחתום בעזרת העכבר או האצבע במסגרת שלהלן</span>
                <SignatureCanvas onCapture={(dataUrl) => setSignatureBase64(dataUrl ? stripSignatureDataUrl(dataUrl) : '')} />
              </div>
            </div>

            {submitError && <span className={styles.error}>{submitError}</span>}
            {previewSubmitted && (
              <span className={styles.previewNotice}>
                תצוגה מקדימה - האישור לא נשמר ולא נשלח מייל. אצל רכז, כאן החלונית נסגרת והעותק החתום נשלח אליו.
              </span>
            )}

            <div className={styles.footer}>
              <button type="button" className={`${styles.submitButton} body-md-semibold`} onClick={handleSubmit} disabled={!canSubmit}>
                {isPending ? 'שומר...' : 'אישור וחתימה'}
              </button>
            </div>
          </div>
        </Content>
      </Portal>
    </Root>
  );
};

export default RegulationsAcceptanceDialog;
